import { Locale } from 'date-fns'

export async function importDateLocale(
  locale: string
): Promise<Locale | undefined> {
  switch (locale) {
    case 'en':
      return (await import('date-fns/locale/en-GB')).enGB
    case 'es':
      return (await import('date-fns/locale/es')).es
    case 'pt':
      return (await import('date-fns/locale/pt')).pt
    case 'fr':
      return (await import('date-fns/locale/fr')).fr
    case 'it':
      return (await import('date-fns/locale/it')).it
    case 'de':
      return (await import('date-fns/locale/de')).de
    default:
      return Promise.resolve(undefined)
  }
}

const SUPPORTED_LOCALES = ['en', 'es', 'pt', 'fr', 'it', 'de'] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

const DEFAULT_LOCALE: SupportedLocale = 'en'

function getBrowserPreferredLocale(): string | null {
  const browserLanguages =
    typeof navigator !== 'undefined'
      ? navigator.languages || [navigator.language]
      : []

  if (browserLanguages.length === 0) {
    return DEFAULT_LOCALE
  }

  for (const browserLang of browserLanguages) {
    if (!browserLang) continue

    const normalizedLang = browserLang.toLowerCase()

    if (SUPPORTED_LOCALES.includes(normalizedLang as SupportedLocale)) {
      return normalizedLang
    }

    const langCode = normalizedLang.split('-')[0]
    if (SUPPORTED_LOCALES.includes(langCode as SupportedLocale)) {
      return langCode
    }
  }

  return null
}

function getUrlLocale(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const url = new URL(window.location.href)

  const localeParam = url.searchParams.get('locale') ?? ''

  const localeMatch = localeParam.match(/^(en|es|pt|fr|it|de)$/)
  return localeMatch ? localeMatch[1] : null
}

export function getCurrentLocale(): string {
  const urlLocale = getUrlLocale()
  if (urlLocale) {
    return urlLocale
  }

  const browserLocale = getBrowserPreferredLocale()
  if (browserLocale) {
    return browserLocale
  }

  return DEFAULT_LOCALE
}

export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
}

export function getSupportedLocales(): readonly SupportedLocale[] {
  return SUPPORTED_LOCALES
}

export async function loadMessages(locale: string) {
  try {
    const messages = await import(`../../locales/${locale}/strings.json`)
    return messages.default
  } catch (error) {
    console.error(`Failed to load messages for locale: ${locale}`, error)
    // Fallback to English
    const fallback = await import(
      `../../locales/${DEFAULT_LOCALE}/strings.json`
    )
    return fallback.default
  }
}
